import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { startPosHeartbeat } from '@/lib/deviceHeartbeat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TodayTile from '@/components/pos/TodayTile';
import { ZatcaStatusDot } from '@/components/pos/ZatcaStatusDot';
import { toast } from 'sonner';
import {
  Search, ShoppingCart,
  X, UtensilsCrossed, LogOut,
  User, Building2, HelpCircle,
  ChevronLeft, Ruler,
} from 'lucide-react';
import { TailorMeasurementsDialog } from '@/components/cashier-pos/TailorMeasurementsDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { promptApproval } from '@/lib/posDiscount';
import { UnsavedCartDialog } from '@/components/pos/dialogs/UnsavedCartDialog';
import { ManagerApprovalDialog } from '@/components/pos/dialogs/ManagerApprovalDialog';
import { resolveBranchModifiers } from '@/lib/menuModifiers';
import { isItemScheduleActive } from '@/lib/itemSchedule';
import { isCapacitorNative } from '@/lib/platformDetect';
import { saveOrShareBlob } from '@/lib/nativeDownload';
import CashierShiftModal from '@/components/CashierShiftModal';
import { PosFirstSaleTour } from '@/components/pos/PosFirstSaleTour';
import ModifiersDialog from '@/components/pos/dialogs/ModifiersDialog';
import SplitPaymentDialog from '@/components/pos/dialogs/SplitPaymentDialog';
import StaffFoodDialog from '@/components/pos/dialogs/StaffFoodDialog';
import VoidLastOrderDialog from '@/components/pos/dialogs/VoidLastOrderDialog';
import CustomerSelectDialog from '@/components/pos/dialogs/CustomerSelectDialog';
import OrderCompleteDialog from '@/components/pos/dialogs/OrderCompleteDialog';
import OrderTypeChooserDialog from '@/components/pos/dialogs/OrderTypeChooserDialog';
import ReprintPickerDialog from '@/components/pos/dialogs/ReprintPickerDialog';
import OrderHistoryDialog from '@/components/pos/dialogs/OrderHistoryDialog';
import RefundOrderDialog from '@/components/pos/dialogs/RefundOrderDialog';
import PrinterSettingsDialog from '@/components/pos/dialogs/PrinterSettingsDialog';
import DayReportDialog from '@/components/pos/dialogs/DayReportDialog';
import OpenChecksDialog from '@/components/pos/dialogs/OpenChecksDialog';
import { SalesReconciliationDialog } from '@/components/sales/SalesReconciliationDialog';
import { normalizePermissions } from '@/config/navigationConfig';
import POSToolbarActions from '@/components/pos/POSToolbarActions';
import { POSBranchPicker } from '@/components/pos/POSBranchPicker';
import { PosSettingsButton } from '@/components/pos/PosSettingsButton';
import { useBranchStore } from '@/stores';
import { StockRequestButton } from '@/components/stock/StockRequestButton';
import { StockRequestPopup } from '@/components/stock/StockRequestPopup';
import { useWebSocket } from '@/hooks/useWebSocket';
import PrinterHealthBadge from '@/components/pos/PrinterHealthBadge';
import { isBridgeAlive, pickPrinter, printReceipt as bridgePrint, printViaCloud, isLikelyIOS, isLikelyMobileCashier, printViaIPrintShare } from '@/lib/printBridge';
import { printViaEposDirect } from '@/lib/eposPrint';
import { logoUrlToDataUri } from '@/lib/thermalLogoOptimizer';
import { resolveReceiptLogo } from '@/lib/receiptLogo';
import POSManifestSwapper from '@/components/POSManifestSwapper';

// iter845 — CashierPOSPage safe UI panel extraction. All business logic
// (state, effects, handlers) stays in this page; presentational chunks
// live under /components/cashier-pos.
import KioskExitPill from '@/components/cashier-pos/KioskExitPill';
import TrainingModeBanner from '@/components/cashier-pos/TrainingModeBanner';
import MenuUpdatedBanner from '@/components/cashier-pos/MenuUpdatedBanner';
import MobileCartFAB from '@/components/cashier-pos/MobileCartFAB';
import PosActionToolbar from '@/components/cashier-pos/PosActionToolbar';
import PosCategoryGrid from '@/components/cashier-pos/PosCategoryGrid';
import PosItemsGrid from '@/components/cashier-pos/PosItemsGrid';
import PosNotesDialog from '@/components/cashier-pos/PosNotesDialog';
import PosDiscountSheet from '@/components/cashier-pos/PosDiscountSheet';
import PosCart from '@/components/cashier-pos/PosCart';
import PauseOnlineOrdersChip from '@/components/cashier-pos/PauseOnlineOrdersChip';

// iter845b — Printer routing / dispatch encapsulated in a hook so this
// page focuses on cashier state + order lifecycle.
import { usePosPrinting } from '@/hooks/usePosPrinting';
// iter845c — Order-creation lifecycle (validation + POST + reset +
// broadcast + print orchestration) lives in its own hook.
import { usePosCheckout } from '@/hooks/usePosCheckout';

import { ReceiptPreview } from '@/components/ReceiptPreview';
import { PrintJobToaster } from '@/components/pos/PrintJobToaster';
import { toLocalYmd } from '@/lib/dateRange';

// iter710 — Persistent "Debug ON" pill. Renders only when the ePOS
// debug toggle in iPad-options is set. Cashiers occasionally see the
// "[debug] …fetch OK" toast and assume something is broken — this
// pill labels it as a debug feature and lets them disable in one tap.
// Cart identity for unsaved-change detection (items, quantities, modifiers, discount).
const cartKey = (lines, disc) => JSON.stringify([Number(disc) || 0, lines.map((l) => [l.item_id, l.quantity, l.modifiers || []])]);

function EposDebugPill() {
  const [on, setOn] = useState(() => {
    try { return typeof window !== 'undefined' && localStorage.getItem('epos_debug') === '1'; }
    catch { return false; }
  });
  // Listen for cross-tab toggles so flipping it off elsewhere updates here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'epos_debug') setOn(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  if (!on) return null;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-semibold hover:bg-amber-200"
      title="Printer debug toasts are enabled. Click to disable."
      onClick={() => {
        try { localStorage.removeItem('epos_debug'); } catch { /* ignored */ }
        setOn(false);
        toast.success('Debug toasts disabled.');
      }}
      data-testid="epos-debug-pill"
    >
      🐛 Debug ON · tap to disable
    </button>
  );
}


export default function CashierPOSPage() {
  // iter825 — register/heartbeat this device for Settings → POS Devices.
  useEffect(() => startPosHeartbeat(), []);
  const navigate = useNavigate();
  const { t: tLang } = useLanguage();
  // iter937 — ws:dashboard bus is now subscribed via handlePosWsMessage below
  // (stock-request popups + waiter/table check events). Single socket.
  const [user, setUser] = useState(null);
  // iter957 — active branch the till is "acting as" (persisted). Drives stock
  // request routing + the Stock button. Defaults to the user's home branch.
  // iter961 — Active branch now comes from the shared branchStore (single
  // source of truth across POS / Stock / Reports); still persisted to the
  // same `pos_active_branch_id` key.
  const activeBranchId = useBranchStore((s) => s.activeBranchId);
  const setActiveBranchId = useBranchStore((s) => s.setActiveBranchId);
  // iter958 — effective branch the till operates as (selected → home).
  const posBranchId = activeBranchId || user?.branch_id || null;
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialogs
  const [showTour, setShowTour] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [showPayment, setShowPayment] = useState(false);
  const [showVoidOrder, setShowVoidOrder] = useState(false);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  // iter746 — Bill output options enabled for this branch.
  const [billOptions, setBillOptions] = useState({ print: true, whatsapp: true, email: true, go_green: true, reprint_resend: true });
  // iter960 — Branch-specific action-tile visibility. Reuses the DB-backed,
  // admin/manager-gated /pos/toolbar-config (config.hidden) so disabling e.g.
  // "Void" or "Discount" persists per branch across devices + logins. Keys:
  // print / reprint / kitchen / void / discount / notes / tags.
  const [actionHidden, setActionHidden] = useState([]);
  const [toolbarBase, setToolbarBase] = useState({ primary: [], overflow: [] });
  const [goGreen, setGoGreen] = useState(false);
  const [showStaffFood, setShowStaffFood] = useState(false);
  const loadToolbarConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/pos/toolbar-config', {
        params: posBranchId ? { branch_id: posBranchId } : {},
      });
      const cfg = data?.config || {};
      setActionHidden(Array.isArray(cfg.hidden) ? cfg.hidden : []);
      setToolbarBase({ primary: cfg.primary || [], overflow: cfg.overflow || [] });
    } catch {
      setActionHidden([]); setToolbarBase({ primary: [], overflow: [] });
    }
  }, [posBranchId]);
  useEffect(() => { loadToolbarConfig(); }, [loadToolbarConfig]);
  // iter962 — Live toggle sync: the backend broadcasts `pos_toolbar_updated`
  // over the existing ws:dashboard bus when a manager changes the ON/OFF
  // toggles on another device, so every till in the branch refetches instantly.
  useEffect(() => {
    const onWs = (e) => {
      const d = e.detail || {};
      if (d.type === 'pos_toolbar_updated' && (!d.branch_id || d.branch_id === posBranchId)) {
        loadToolbarConfig();
      }
    };
    window.addEventListener('ws:dashboard', onWs);
    return () => window.removeEventListener('ws:dashboard', onWs);
  }, [posBranchId, loadToolbarConfig]);
  // iter961 — Manager toggles a toolbar button ON/OFF from POS Settings. Reuses
  // /pos/toolbar-config; preserves the existing primary/overflow layout so the
  // OTHER header toolbar (POSToolbarActions) is untouched.
  const canEditToolbar = ['admin', 'manager', 'owner', 'super_admin'].includes(user?.role);
  const toggleToolbarButton = async (key) => {
    if (!canEditToolbar) { toast.error('Managers / admins only'); return; }
    const prev = actionHidden;
    const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    setActionHidden(next);
    try {
      await api.post('/pos/toolbar-config', {
        branch_id: posBranchId || null,
        config: { primary: toolbarBase.primary, overflow: toolbarBase.overflow, hidden: next },
      });
      toast.success('POS buttons updated for this branch');
    } catch { setActionHidden(prev); toast.error('Failed to save POS buttons'); }
  };

  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount');
  const [orderType, setOrderType] = useState('dine_in');
  const [tableNumber, setTableNumber] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [notes, setNotes] = useState('');
  // iter916 — Tailor branch: measurements ride the sale as metadata.
  const [saleMetadata, setSaleMetadata] = useState(null);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  // iter893 — One-time order-type chooser. `orderTypeChosen` gates the
  // OrderTypeChooserDialog: false while the current invoice hasn't
  // picked yet, true after a pick. Auto-resets to false when the cart
  // clears (i.e. after every completed order) so the next invoice
  // gets its own explicit pick. Editing an existing order pre-fills
  // the choice, so the chooser stays hidden for the whole edit.
  const [orderTypeChosen, setOrderTypeChosen] = useState(false);

  // Order History
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [showDaySummary, setShowDaySummary] = useState(false);
  const [closeoutMode, setCloseoutMode] = useState(false);
  // Phase B — POS Sales Reconciliation (reuses the shared SalesReconciliationDialog).
  const [showReconcile, setShowReconcile] = useState(false);
  // Waiter/Table — Cashier Open Checks + the id of the check being finalized.
  const [showOpenChecks, setShowOpenChecks] = useState(false);
  const [activeCheckId, setActiveCheckId] = useState(null);
  const loadedCartKeyRef = useRef(cartKey([], 0));
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const [switchSaving, setSwitchSaving] = useState(false);
  const [approvalReq, setApprovalReq] = useState(null);
  const requestApproval = useCallback((opts) => new Promise((resolve) => setApprovalReq({ ...opts, resolve })), []);
  const [openChecksRefresh, setOpenChecksRefresh] = useState(0);
  const wsAlertSeenRef = useRef({});
  const [openChecksCount, setOpenChecksCount] = useState(0);
  // The signed-in user's full profile (role + permissions) fetched via /auth/me
  // so we can permission-gate the Reconciliation action. Backend stays authoritative.
  const [posMe, setPosMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  // Printer
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printerForm, setPrinterForm] = useState({ name: '', type: 'receipt', station: '', ip_address: '', port: 9100, paper_width: '80mm', is_default: false, auto_print: false, copies: 1, branch_id: null, print_mode: 'auto' });
  const [receiptOrderId, setReceiptOrderId] = useState(null);
  const [editingPrinter, setEditingPrinter] = useState(null);

  // Branch tax info
  const [branchTax, setBranchTax] = useState({ tax_rate: 15, tax_name: 'VAT', currency_code: 'SAR' });
  // iter703 — Upsell suggestions for the current cart.
  const [upsellSuggestions, setUpsellSuggestions] = useState([]);
  // iter616 — Branch training mode: when active (ISO timestamp in the
  // future) every print operation is forced into 'dialog' mode so new
  // staff can see what would print before paper actually moves.
  const [trainingUntil, setTrainingUntil] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [branding, setBranding] = useState(null);
  const isTrainingActive = !!(trainingUntil && new Date(trainingUntil) > new Date());

  // iter599 — Robust kiosk mode. Triggered by ?kiosk=1 in the URL.
  // Works across desktop / tablet / mobile without requiring pop-ups:
  //   • Sets `body[data-kiosk="1"]` so global CSS can hide scrollbars
  //     etc. (no-op today; in place for future polish).
  //   • Requests browser fullscreen on the FIRST user gesture (any
  //     pointer down) because iOS Safari + Android Chrome reject
  //     non-gesture fullscreen requests.
  //   • Blocks the browser back-button from leaving the POS by pushing
  //     a sentinel history entry and re-pushing it on every popstate.
  //   • Renders a small "Exit Kiosk" pill in the corner so the cashier
  //     can leave cleanly without resorting to the OS gesture.
  const [kioskMode, setKioskMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('kiosk') === '1';
  });

  // iter828 — per-device Density toggle (Compact ↔ Comfortable). Stored
  // in localStorage so each POS terminal (waiter's 7", counter's 10",
  // manager's laptop) keeps its own preferred tile size.
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') return 'compact';
    return localStorage.getItem('pos_density') || 'compact';
  });
  const toggleDensity = () => {
    const next = density === 'compact' ? 'comfortable' : 'compact';
    setDensity(next);
    try { localStorage.setItem('pos_density', next); } catch (e) { /* private mode */ }
  };
  const isComfortable = density === 'comfortable';

  // iter831 — Kitchen printing toggle. Cashiers can disable prep-ticket
  // printing entirely (small takeaway spots that just want a customer
  // receipt) or enable it (dine-in restaurants). Persisted per device.
  const [kitchenPrinting, setKitchenPrinting] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('kitchen_printing_enabled') !== '0';
  });
  const toggleKitchenPrinting = () => {
    const next = !kitchenPrinting;
    setKitchenPrinting(next);
    try {
      localStorage.setItem('kitchen_printing_enabled', next ? '1' : '0');
    } catch (_e) { /* ignore */ }
    toast.success(next ? 'Kitchen tickets ENABLED' : 'Kitchen tickets DISABLED — only customer receipt will print');
  };

  // iter833 — Auto-print receipt after payment. When ON we fire the
  // receipt via the printer bridge as soon as the order is created and
  // skip the OrderCompleteDialog. When OFF we show the preview dialog
  // so the cashier can decide.
  // iter893 — Enforced ON by default. User asked for fastest checkout,
  // so we now HARD-DEFAULT to true even if the localStorage flag was
  // previously flipped off. Toggle still exists in the settings row
  // for advanced users who explicitly want the preview dialog.
  const [autoPrintOnPay, setAutoPrintOnPay] = useState(() => {
    if (typeof window === 'undefined') return true;
    const raw = localStorage.getItem('auto_print_on_pay');
    // Treat missing key OR any non-'0' value as ON. Only an explicit '0'
    // (set via the toggle) disables it.
    return raw !== '0';
  });
  // iter893 — Migrate legacy stored '0' -> re-enable on mount so any
  // cashier device that ever toggled it off years ago comes back to
  // the fast-checkout default. If they still want it off they can
  // toggle again after login.
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && !localStorage.getItem('auto_print_on_pay')) {
        localStorage.setItem('auto_print_on_pay', '1');
      }
    } catch (_e) { /* ignore */ }
  }, []);
  const toggleAutoPrintOnPay = () => {
    const next = !autoPrintOnPay;
    setAutoPrintOnPay(next);
    try {
      localStorage.setItem('auto_print_on_pay', next ? '1' : '0');
    } catch (_e) { /* ignore */ }
    toast.success(next ? 'Auto-print ENABLED — receipts will print immediately' : 'Auto-print DISABLED — preview dialog will show');
  };

  useEffect(() => {
    if (!kioskMode) return;
    document.body.setAttribute('data-kiosk', '1');

    // Fullscreen-on-first-gesture (iOS/Android safe). One-shot listener.
    const tryFullscreen = () => {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req && !document.fullscreenElement) {
        try { req.call(el); } catch (e) { /* iOS Safari: silently ignore */ }
      }
      window.removeEventListener('pointerdown', tryFullscreen);
      window.removeEventListener('keydown', tryFullscreen);
    };
    window.addEventListener('pointerdown', tryFullscreen, { once: true });
    window.addEventListener('keydown', tryFullscreen, { once: true });

    // Back-button trap. Push a sentinel so the FIRST back press lands
    // on the same URL — onpopstate re-pushes so subsequent presses
    // also stay. The "Exit Kiosk" pill is the only sanctioned way out.
    window.history.pushState({ kiosk: true }, '', window.location.href);
    const onPopState = () => {
      window.history.pushState({ kiosk: true }, '', window.location.href);
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      document.body.removeAttribute('data-kiosk');
      window.removeEventListener('pointerdown', tryFullscreen);
      window.removeEventListener('keydown', tryFullscreen);
      window.removeEventListener('popstate', onPopState);
    };
  }, [kioskMode]);

  const exitKioskMode = () => {
    try { (document.exitFullscreen || document.webkitExitFullscreen)?.call(document); } catch (e) { /* not in fullscreen — fine */ }
    setKioskMode(false);
    // Drop the ?kiosk=1 from the URL so a refresh doesn't re-enter.
    const url = new URL(window.location.href);
    url.searchParams.delete('kiosk');
    window.history.replaceState({}, '', url.toString());
  };

  // Check auth — with QA bypass support (iter374). When the env flag
  // CASHIER_PIN_BYPASS=1 is on AND the user holds a regular admin JWT,
  // POST /cashier/login-via-jwt to mint a cashier session without the
  // 4-digit PIN. This unblocks Playwright/testing-agent coverage for
  // /cashier-pos which would otherwise sit behind a PIN keypad.
  useEffect(() => {
    let cancelled = false;
    const initAuth = async () => {
      const token = localStorage.getItem('cashier_token');
      const userData = localStorage.getItem('cashier_user');
      if (token && userData) {
        setUser(JSON.parse(userData));
        return;
      }
      // Bypass attempt: do we have a normal app JWT? If yes, swap it
      // for a cashier token. The endpoint returns 403 if the env flag
      // is off, in which case we fall back to the PIN flow normally.
      const adminToken = localStorage.getItem('token');
      if (adminToken) {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cashier/login-via-jwt`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (r.ok) {
            const data = await r.json();
            if (cancelled) return;
            localStorage.setItem('cashier_token', data.access_token);
            localStorage.setItem('cashier_user', JSON.stringify(data.user || {}));
            setUser(data.user || {});
            return;
          }
        } catch { /* fall through to PIN gate */ }
      }
      if (!cancelled) navigate('/cashier');
    };
    initAuth();
    return () => { cancelled = true; };
  }, [navigate]);

  // Phase B — load the signed-in user's full profile (role + permissions) so
  // we can permission-gate the Sales Reconciliation action. We pass the live
  // cashier_token explicitly (/auth/me isn't a POS-session path) so a PIN-only
  // session authenticates as the actual cashier. Backend require_permission
  // stays the sole authority; this only decides whether to SHOW the entry.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('cashier_token');
        if (!token) return;
        const { data } = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setPosMe(data);
      } catch { /* non-blocking — reconcile entry simply stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Mirrors backend has_permission(sales, write): admin bypasses; otherwise the
  // user needs sales='write' and (if allowed_modules is set) sales in scope.
  // View (enter actual + see difference) needs sales read; recording a difference needs sales write.
  const reconcileAccess = useMemo(() => {
    if (!posMe) return { view: false, record: false };
    if ((posMe.role || '').toLowerCase() === 'admin') return { view: true, record: true };
    const allowed = posMe.allowed_modules;
    if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes('sales') && !allowed.includes('admin')) {
      return { view: false, record: false };
    }
    const lvl = normalizePermissions(posMe.permissions)['sales'];
    return { view: lvl === 'read' || lvl === 'write', record: lvl === 'write' };
  }, [posMe]);
  const canReconcile = reconcileAccess.view;


  // Refunds: server enforces POS write + branch + manager PIN; the button is only a convenience gate.
  const canRefund = useMemo(() => !!posMe && ((posMe.role || '').toLowerCase() === 'admin'
    || normalizePermissions(posMe.permissions)['pos'] === 'write'), [posMe]);
  const [refundOrder, setRefundOrder] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      // iter905 — Always pass branch_id when fetching categories + menu
      // so a cashier on Branch A never sees Branch B's items even if the
      // backend's user.branch_id fallback is missing. Reads from the
      // stashed cashier_user in localStorage (populated on login).
      let _cachedBranch = null;
      try {
        const _raw = localStorage.getItem('cashier_user');
        // iter958 — prefer the POS-selected acting branch so a full rebind
        // (menu/items/prices/branding) follows the header Branch Picker.
        _cachedBranch = localStorage.getItem('pos_active_branch_id')
          || (_raw ? (JSON.parse(_raw)?.branch_id || null) : null);
      } catch { /* ignore parse errors */ }
      const menuParams = _cachedBranch ? { branch_id: _cachedBranch } : {};
      const [catRes, menuRes, custRes, statsRes, shiftRes, taxRes] = await Promise.all([
        api.get('/cashier/categories', { headers, params: menuParams }),
        api.get('/cashier/menu', { headers, params: menuParams }),
        api.get('/cashier/customers', { headers }),
        api.get('/cashier/stats', { headers }),
        api.get('/cashier/shift/current', { headers }).catch(() => ({ data: null })),
        api.get('/cashier/branch-tax-info', { headers, params: menuParams }).catch(() => ({ data: { tax_rate: 15, tax_name: 'VAT', currency_code: 'SAR' } })),
      ]);
      setCategories(catRes.data);
      setMenuItems(menuRes.data);
      setCustomers(custRes.data);
      setStats(statsRes.data);
      setCurrentShift(shiftRes.data);
      if (taxRes.data) setBranchTax(taxRes.data);
      // iter616 — Load the cashier's branch to read training_mode_until.
      try {
        // Read directly from cashier_user since fetchData has empty deps
        // (intentional — we re-trigger via the `if (user) fetchData()`
        // effect on the next render).
        const raw = localStorage.getItem('cashier_user');
        const me = raw ? JSON.parse(raw) : null;
        const bid = _cachedBranch || me?.branch_id;  // iter958 — follow selected branch
        if (bid) {
          const { data: bs } = await api.get('/branches', { headers });
          const branch = (bs || []).find((b) => b.id === bid);
          setTrainingUntil(branch?.training_mode_until || null);
          // iter628 — Capture branch-level receipt header/footer for thermal print.
          setBranchInfo(branch || null);
        }
        // iter630 — Load branding (thermal logo URL) once.
        // iter775 — branch-aware: pass branch_id so this branch's own
        // contact overrides (phone/address/email/VAT) print on its receipts.
        try {
          const { data: br } = await api.get('/pdf-exports/branding', { params: bid ? { branch_id: bid } : {}, headers });
          // iter915 — Pre-optimise logo → Data URI BEFORE any print path
          // touches it. Prevents SecurityError / tainted-canvas on the
          // ePOS raster path when the logo lives on a different origin
          // without CORS headers. `logoUrlToDataUri` is idempotent (a
          // data URI passes through unchanged).
          try {
            const paperMm = 80; // safe default; raster path re-scales anyway
            // Shared resolver (Branch Logo → Global Logo) with the built-in
            // 10x10px size guard so blank dots never block the real logo.
            const rawLogo = br ? await resolveReceiptLogo(br) : '';
            const fromBranch = !!(br && br.branch_logo_url && rawLogo === br.branch_logo_url);
            if (rawLogo && !String(rawLogo).startsWith('data:')) {
              const absoluteUrl = /^https?:\/\//i.test(rawLogo)
                ? rawLogo
                : `${process.env.REACT_APP_BACKEND_URL || ''}${rawLogo.startsWith('/uploads/') ? '/api' : ''}${rawLogo.startsWith('/') ? '' : '/'}${rawLogo}`;
              const dataUri = await logoUrlToDataUri(absoluteUrl, paperMm);
              if (dataUri) {
                br.thermal_logo_url = dataUri;
                br.logo_url = dataUri;
                if (fromBranch) {
                  br.branch_logo_url = dataUri;
                  // Keep branchInfo in sync so posPrint's branch-first
                  // chain picks up the optimised data URI too.
                  setBranchInfo((prev) => (prev ? { ...prev, branch_logo_url: dataUri } : prev));
                }
              }
            }
          } catch (_e) { /* logo optional — receipt still prints */ }
          setBranding(br || null);
        } catch { /* branding optional */ }
      } catch { /* non-blocking */ }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  // iter829 — Refresh menu items whenever the POS tab regains focus.
  // Without this, a cashier who edits an item's price / VAT-inclusive
  // flag in another tab and comes back to the POS keeps the stale
  // snapshot from initial load, and totals are computed against the
  // old flag until a manual page reload. Focus / visibility refetch
  // is quick (menu is <500 items) and keeps display in sync with
  // whatever the manager just changed.
  useEffect(() => {
    if (!user) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') { fetchData(); fetchOrders(); }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [user, fetchData]);

  // iter830 — Live "menu updated" banner. Polls /cashier/menu-version
  // every 30s; when the version string changes vs the version we saw
  // at last full fetch, we surface a small banner asking the cashier
  // to reload. One tap → fetchData() → banner disappears.
  const [menuVersion, setMenuVersion] = useState('');       // baseline captured at fetch time
  const [menuChanged, setMenuChanged] = useState(false);    // banner visible
  const [menuChangedAt, setMenuChangedAt] = useState(null); // for the "3 min ago" copy

  const captureVersion = useCallback(async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      const { data } = await api.get('/cashier/menu-version', { headers: { Authorization: `Bearer ${token}` } });
      setMenuVersion(data?.version || '');
      setMenuChanged(false);
    } catch { /* best-effort */ }
  }, []);

  // Refresh the baseline every time we do a full fetchData.
  useEffect(() => {
    if (user) captureVersion();
  }, [user, captureVersion]);

  // Poll every 30s.
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(async () => {
      if (document.hidden) return; // skip while the tab is hidden
      try {
        const token = localStorage.getItem('cashier_token');
        const { data } = await api.get('/cashier/menu-version', { headers: { Authorization: `Bearer ${token}` } });
        if (data?.version && menuVersion && data.version !== menuVersion) {
          setMenuChanged(true);
          setMenuChangedAt(new Date());
        }
      } catch { /* offline, ignore */ }
    }, 30000);
    return () => clearInterval(iv);
  }, [user, menuVersion]);

  const acceptMenuUpdate = useCallback(async () => {
    await fetchData();
    await captureVersion();
    setMenuChanged(false);
  }, [fetchData, captureVersion]);

  // iter673 — Auto-load printers on mount so the FIRST payment of the
  // session can use ePOS-Direct / iPrint immediately. Previously printers
  // were only loaded lazily, causing the first order to silently skip
  // those paths.
  useEffect(() => {
    if (user) fetchPrinters();
  }, [user]);

  // iter746 — Load this branch's enabled bill-output options.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = localStorage.getItem('cashier_token');
        const bid = user?.branch_id || '';
        const { data } = await api.get(`/cashier/bill-delivery-settings${bid ? `?branch_id=${bid}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
        if (data?.options) setBillOptions(data.options);
      } catch { /* keep defaults (all enabled) */ }
    })();
  }, [user]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      // iter — strictly scope Order History to the POS's current active branch
      // so orders from other branches never appear on this till.
      const { data } = await api.get('/cashier/orders', {
        headers,
        params: posBranchId ? { branch_id: posBranchId } : {},
      });
      setOrders(data || []);
    } catch { setOrders([]); }
    finally { setLoadingOrders(false); }
  };

  // Printer management
  const fetchPrinters = async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      // iter958 — branch-scoped so switching the till's branch reloads that
      // branch's printers. Backend ignores the param if unsupported.
      const bid = localStorage.getItem('pos_active_branch_id') || null;
      const { data } = await api.get('/cashier/printers', {
        headers: { Authorization: `Bearer ${token}` },
        params: bid ? { branch_id: bid } : {},
      });
      setPrinters(data || []);
    } catch {}
  };

  // iter958 — TRUE branch terminal: when the acting branch changes, re-fetch
  // the menu/items/prices/branding AND this branch's printers.
  useEffect(() => {
    if (!user) return;
    fetchData();
    fetchPrinters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  // iter604 — listen for the Quick Setup dialog so newly-created
  // printers show up without manually refreshing.
  useEffect(() => {
    const onReload = () => { fetchPrinters(); };
    window.addEventListener('printers:reload', onReload);
    return () => window.removeEventListener('printers:reload', onReload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual drawer-kick — logs an audit event and (when a print bridge is
  // wired) sends the ESC/P open-drawer command to the receipt printer.
  // The audit log is what shift reconciliation actually checks against;
  // the physical kick is best-effort.
  const openCashDrawer = async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      const branch_id = user?.branch_id || null;
      const { data } = await api.post(
        '/cashier/drawer/kick',
        { reason: 'manual', branch_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tgt = data?.printer?.name ? ` → ${data.printer.name}` : '';
      if (data?.printer?.ip_address || data?.printer?.connection_type === 'sunmi') {
        const { kickNativeDrawer } = await import('@/lib/nativeThermalPrint');
        await kickNativeDrawer({ type: data.printer.connection_type, address: data.printer.ip_address, port: data.printer.port || 9100 });
      }
      toast.success(`Drawer opened${tgt}`, { duration: 2500 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open drawer');
    }
  };

  const savePrinter = async () => {
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingPrinter) {
        await api.put(`/cashier/printers/${editingPrinter.id}`, printerForm, { headers });
        toast.success('Printer updated');
      } else {
        await api.post('/cashier/printers', printerForm, { headers });
        toast.success('Printer added');
      }
      fetchPrinters();
      setEditingPrinter(null);
      setPrinterForm({ name: '', type: 'receipt', station: '', ip_address: '', port: 9100, paper_width: '80mm', is_default: false, auto_print: false, copies: 1, branch_id: null, print_mode: 'auto' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const deletePrinter = async (id) => {
    if (!window.confirm('Delete this printer?')) return;
    try {
      const token = localStorage.getItem('cashier_token');
      await api.delete(`/cashier/printers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Printer removed');
      fetchPrinters();
    } catch { toast.error('Failed to delete'); }
  };
  const testPrinter = async (id) => {
    try {
      const token = localStorage.getItem('cashier_token');
      const { data } = await api.post(`/cashier/printers/${id}/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const printer = data?.printer;
      const payload = data?.test_payload;
      if (!printer?.ip_address || !payload) {
        toast.info(data?.message || 'Test queued');
        return;
      }
      // iter611 — Respect the printer's print_mode setting:
      //   "silent" → bridge only; never fall back
      //   "dialog" → always use the OS dialog
      //   "auto" or unset → bridge first, dialog fallback (default)
      const mode = printer.print_mode || 'auto';
      const tryDialog = () => {
        const lines = [
          `<div style="text-align:center;font-weight:bold">TEST TICKET</div>`,
          `<div style="text-align:center">${printer.name}</div>`,
          `<div style="text-align:center">${printer.ip_address}:${printer.port || 9100}</div>`,
          `<hr/>`,
          `<div>If you can read this, this printer is wired up correctly.</div>`,
          `<hr/>`,
          `<div style="text-align:center;font-size:10px;color:#666">Print mode: ${mode}</div>`,
        ].join('');
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        iframe.srcdoc = `<!doctype html><html><head><style>
          @page { size: 80mm auto; margin: 2mm; }
          body { font-family: monospace; width: 76mm; padding: 4mm; font-size: 12px; }
        </style></head><body>${lines}<script>window.onload=()=>{setTimeout(()=>{window.print();},150)}</script></body></html>`;
        document.body.appendChild(iframe);
        setTimeout(() => { try { iframe.remove(); } catch { /* */ } }, 30000);
      };
      if (mode === 'dialog') {
        tryDialog();
        toast.info('Print mode: Dialog — opening OS print dialog.');
        return;
      }
      // iter648 — iPad / iOS: skip localhost probe, route via Cloud Relay.
      // iter675 — Prefer ePOS-Direct on mobile cashier devices when the
      //           printer has an IP. Falls through to Cloud Relay only
      //           if ePOS fails or the printer is IP-less.
      if (isLikelyMobileCashier()) {
        if (printer?.ip_address) {
          const r = await printViaEposDirect(printer, payload);
          if (r.ok) {
            toast.success(`Test sent → ${printer.name} (ePOS Direct)`);
            return;
          }
          toast.error(r.msg || `ePOS Direct failed for ${printer.name}. Check the cert acceptance step.`);
          return;
        }
      }
      if (isLikelyIOS()) {
        const r = await printViaCloud(api, {
          printerId: printer.id,
          branchId: user?.branch_id,
          payload: { ...payload, ip: printer.ip_address, port: printer.port || 9100 },
        });
        if (r.ok) toast.success(`Test queued → ${printer.name} (Cloud Relay)`);
        else if (r.status === 409) toast.error('No Bridge registered. Add a printer IP and use ePOS Direct (Settings → Printers).');
        else toast.error(`Cloud Relay failed: ${r.error || 'unknown'}`);
        return;
      }
      const alive = await isBridgeAlive();
      if (alive) {
        const r = await bridgePrint(printer, payload, { kitchen: printer.type === 'kitchen' });
        if (r.ok) toast.success(`Test printed → ${printer.name}`);
        else toast.error(`Test failed: ${r.data?.error || 'check printer IP'}`);
        return;
      }
      if (mode === 'silent') {
        toast.error(`Silent mode requires the Print Bridge. Install it or switch ${printer.name} to "Auto" / "Dialog" mode.`);
        return;
      }
      // Auto mode + no bridge → use OS dialog as planned fallback.
      tryDialog();
      toast.info('No bridge detected — opening OS print dialog.');
    } catch { toast.error('Test failed'); }
  };

  const handleDeleteOrder = async (orderId, isOpenCheck = false) => {
    if (!window.confirm(isOpenCheck ? 'Void this open check?' : 'Are you sure you want to void this order?')) return;
    // Server enforces manager PIN for every void (same PIN check as Void Last Order).
    const approval = await requestApproval({ title: isOpenCheck ? 'Void open check' : 'Void order', needReason: true,
      detail: 'Manager PIN and a reason are required to void.' });
    if (!approval) return;
    const managerPin = approval.pin;
    const reason = approval.reason;
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      await api.delete(`/cashier/orders/${orderId}`, { headers, data: { manager_pin: managerPin, reason } });
      toast.success('Order voided');
      if (orderId === activeCheckId) { setCart([]); setDiscount(0); setNotes(''); setActiveCheckId(null); }
      setOpenChecksRefresh((n) => n + 1);
      fetchOrders();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to void order'); }
  };

  // Waiter/Table realtime — reuse the existing ws:dashboard bus. When a waiter
  // requests payment (or opens/updates a check) on THIS branch, alert the
  // cashier and refresh the Open Checks list. No new WebSocket system.
  const handlePosWsMessage = useCallback((msg) => {
    const type = msg?.type || msg?.event;
    const payload = msg?.data || msg?.payload || msg || {};
    if (!['check_payment_requested', 'check_opened', 'check_items_added', 'check_completed', 'check_voided', 'check_items_cancelled'].includes(type)) return;
    if (posBranchId && payload.branch_id && payload.branch_id !== posBranchId) return; // same-branch only
    setOpenChecksRefresh((n) => n + 1);
    // One alert per check+event within 5s (duplicate WS deliveries are ignored).
    const dedupKey = `${type}:${payload.order_id || payload.check_id || payload.table_number || ''}`;
    const nowTs = Date.now();
    if (nowTs - (wsAlertSeenRef.current[dedupKey] || 0) < 5000) return;
    wsAlertSeenRef.current[dedupKey] = nowTs;
    if (type === 'check_opened' || type === 'check_items_added') {
      const where = payload.table_number ? `Table ${payload.table_number}` : (payload.check_label || 'Held bill');
      toast.info(`${where} · ${type === 'check_opened' ? 'New check opened' : 'Items added'}`, {
        duration: 5000,
        action: { label: 'Open', onClick: () => setShowOpenChecks(true) },
      });
    }
    if (type === 'check_payment_requested') {
      toast.info(`Table ${payload.table_number ?? ''} · ${payload.check_label || 'Check'} — Payment Requested`, {
        duration: 8000,
        action: { label: 'Open', onClick: () => setShowOpenChecks(true) },
      });
    }
  }, [posBranchId]);
  useWebSocket(handlePosWsMessage);

  // Open Checks count on the toolbar — same GET /cashier/orders?status=open the dialog uses.
  useEffect(() => {
    if (!posBranchId) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${localStorage.getItem('cashier_token')}` };
    api.get('/cashier/orders', { params: { branch_id: posBranchId, status: 'open' }, headers })
      .then(({ data }) => { if (!cancelled) setOpenChecksCount((Array.isArray(data) ? data : (data?.orders || [])).length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [posBranchId, openChecksRefresh]);

  // Hold bill (telephone/takeaway, no table) → existing open-check record; resume via Open Checks.
  const holdingRef = useRef(false);
  const handleHoldBill = async (approvals = {}, customerArg = null) => {
    if (!cart.length) { toast.error('Cart is empty'); return false; }
    if (holdingRef.current) return false;
    const customer = customerArg ?? window.prompt('Customer name or phone for this held bill (optional):', '') ?? null;
    if (customer === null) return false;
    holdingRef.current = true;
    try {
      const isPhone = /^[+\d][\d\s-]{5,}$/.test(customer.trim());
      const headers = { Authorization: `Bearer ${localStorage.getItem('cashier_token')}` };
      const { data: held } = await api.post('/cashier/orders/hold', {
        branch_id: posBranchId, order_id: activeCheckId || undefined,
        items: cart, order_type: orderType, table_number: tableNumber || null, notes,
        customer_name: isPhone ? '' : customer.trim(), customer_phone: isPhone ? customer.trim() : '',
        client_request_id: window.crypto?.randomUUID?.() || `${Date.now()}`, ...approvals,
      }, { headers });
      toast.success('Bill held — resume it from Open Checks');
      setCart([]); setDiscount(0); setNotes(''); setTableNumber(''); setActiveCheckId(null);
      setOpenChecksRefresh((n) => n + 1);
      return held || true;
    } catch (err) {
      holdingRef.current = false;
      const approved = await promptApproval(err, approvals, requestApproval);
      if (approved) return Object.keys(approved).length ? handleHoldBill(approved, customer) : false;
      toast.error(err.response?.data?.detail || 'Failed to hold bill');
      return false;
    } finally { holdingRef.current = false; }
  };

  // Universal POS — when the branch industry offers only ONE order type
  // (e.g. retail "counter"), auto-select it and skip the restaurant-style
  // chooser so non-restaurant businesses get a frictionless checkout.
  useEffect(() => {
    const types = branchTax.order_types;
    if (Array.isArray(types) && types.length === 1 && !orderTypeChosen && !editingOrder) {
      setOrderType(types[0]);
      setOrderTypeChosen(true);
    }
  }, [branchTax, orderTypeChosen, editingOrder]);

  // Cashier opens an existing waiter/table check → load the SAME pos_order into
  // the cart (reuses the edit-order loader) and mark it the active check so the
  // normal payment flow finalizes it via order_id (no copy, idempotent).
  const handleOpenCheck = (order) => {
    const orderTaxRate = (branchTax.tax_rate || 0) / 100;
    setActiveCheckId(order.id);
    setEditingOrder(null);
    const lines = (order.items || []).map((item) => {
      // Waiter-fired lines don't carry price_includes_tax — take it from the menu item (same flag the server uses).
      const menuItem = menuItems.find((m) => m.id === item.item_id);
      const inclusive = !!(menuItem?.price_includes_tax ?? item.price_includes_tax);
      const qty = item.quantity || 1;
      // Waiter-fired items carry `price` (not unit_price/subtotal). Fall back so
      // the cart hydrates at the real amount instead of SAR 0.00.
      const unit = (item.unit_price ?? item.price) || 0;
      const modTotal = item.modifier_total || (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
      const baseSubtotal = (item.subtotal ?? ((unit + modTotal) * qty)) || 0;
      // Open-check lines store the GROSS line; only a server-flagged line stores the pre-tax base.
      const grossSubtotal = item.price_includes_tax && orderTaxRate > 0 ? baseSubtotal * (1 + orderTaxRate) : baseSubtotal;
      return {
        item_id: item.item_id, name: item.name, name_ar: item.name_ar,
        unit_price: unit, modifiers: item.modifiers || [],
        modifier_total: modTotal,
        quantity: qty, subtotal: grossSubtotal, price_includes_tax: inclusive,
      };
    });
    setCart(lines);
    loadedCartKeyRef.current = cartKey(lines, order.discount || 0);
    setDiscount(order.discount || 0);
    setDiscountType('amount'); // stored discount is always the monetary amount
    setOrderType(order.order_type || 'dine_in');
    setTableNumber(order.table_number || '');
    setNotes(order.notes || '');
    setOrderTypeChosen(true);
    toast.success(`Opened Table ${order.table_number ?? ''} ${order.check_label || ''} — complete payment to finalize`);
  };

  const handleEditOrder = (order) => {
    // Load order items into cart for editing.
    // iter820 — backend stores `subtotal` as the PRE-TAX base for
    // inclusive items; cart-line semantics elsewhere use GROSS totals.
    // Convert on load so the cart calc stays consistent.
    setEditingOrder(order);
    const orderTaxRate = (branchTax.tax_rate || 0) / 100;
    const lines = order.items.map(item => {
      const inclusive = !!item.price_includes_tax;
      const qty = item.quantity || 1;
      // Some orders (online/aggregator imports, waiter fires) carry `price`
      // but no `unit_price`/`subtotal`. unit_price is the GROSS line unit, so
      // the gross line is unit×qty (+ modifiers) regardless of tax mode —
      // this reconstructs the exact value a stored subtotal would yield,
      // preventing the cart/payment total hydrating at SAR 0.00
      // ("Full · SAR 0.00") without double-counting VAT.
      const unit = (item.unit_price ?? item.price) || 0;
      const modTotal = item.modifier_total || (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
      const grossLine = (unit + modTotal) * qty;
      const storedBase = item.subtotal;
      const grossSubtotal = (storedBase != null && storedBase > 0)
        ? (inclusive && orderTaxRate > 0 ? storedBase * (1 + orderTaxRate) : storedBase)
        : grossLine;
      return {
        item_id: item.item_id, name: item.name, name_ar: item.name_ar,
        unit_price: unit, modifiers: item.modifiers || [],
        modifier_total: modTotal,
        quantity: qty, subtotal: grossSubtotal,
        price_includes_tax: inclusive,
      };
    });
    setCart(lines);
    loadedCartKeyRef.current = cartKey(lines, order.discount || 0);
    setPaymentMethod(order.payment_method || 'cash');
    setDiscount(order.discount || 0);
    setDiscountType('amount'); // stored discount is always the monetary amount
    setOrderType(order.order_type || 'dine_in');
    setTableNumber(order.table_number || '');
    setNotes(order.notes || '');
    setOrderTypeChosen(true); // iter893 — editing skips the chooser prompt.
    setShowOrderHistory(false);
  };

  // Saves the cart back to the order being edited OR the open check/held bill loaded from Open Checks.
  const handleSaveEdit = async (approvals = {}) => {
    const targetId = editingOrder?.id || activeCheckId;
    if (!targetId || cart.length === 0) return false;
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payment = editingOrder
        ? { payment_method: paymentMethod, payment_details: [{ mode: paymentMethod, amount: total }] } : {};
      await api.put(`/cashier/orders/${targetId}`, {
        items: cart.map(c => ({ item_id: c.item_id, quantity: c.quantity, modifiers: c.modifiers })),
        discount, discount_type: discountType, ...approvals, ...payment,
        order_type: orderType, table_number: tableNumber || null, notes
      }, { headers });
      toast.success(editingOrder ? `Order #${editingOrder.order_number} updated!` : 'Check saved');
      setEditingOrder(null);
      setActiveCheckId(null);
      setCart([]);
      setDiscount(0);
      setNotes('');
      setTableNumber('');
      setOpenChecksRefresh((n) => n + 1);
      fetchData();
      return true;
    } catch (err) {
      const approved = await promptApproval(err, approvals, requestApproval);
      if (approved) return Object.keys(approved).length ? handleSaveEdit(approved) : false;
      toast.error(err.response?.data?.detail || 'Failed to update order');
      return false;
    }
  };

  // Switching bills never silently overwrites unsaved cart changes (Save / Discard / Stay).
  const isCartDirty = cart.length > 0 && cartKey(cart, discount) !== loadedCartKeyRef.current;
  const guardSwitch = (load) => (order) => {
    if (isCartDirty && order?.id !== (editingOrder?.id || activeCheckId)) { setPendingSwitch({ load, order }); return; }
    load(order);
  };
  const handleSwitchSave = async () => {
    const p = pendingSwitch;
    setSwitchSaving(true);
    const ok = (editingOrder || activeCheckId) ? await handleSaveEdit() : await handleHoldBill();
    setSwitchSaving(false);
    if (ok) { setPendingSwitch(null); p.load(p.order); }
  };


  // Filter menu items (schedule check uses shared canonical helper: lib/itemSchedule.js)
  const filteredItems = menuItems.filter(item => {
    // Schedule-based filtering
    if (item.schedule?.enabled) {
      const active = isItemScheduleActive(item);
      if (!active && item.schedule.unavailable_behavior === 'hide') return false;
    }
    if (selectedCategory !== 'all' && selectedCategory !== 'popular') {
      if (item.category !== selectedCategory) return false;
    }
    if (selectedCategory === 'popular' && !item.tags?.includes('popular')) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.name_ar?.includes(q);
    }
    return true;
  });

  // Cart calculations — iter820: honor per-item `price_includes_tax`.
  // For "inclusive" items the on-item price is GROSS (already contains
  // VAT); we back-derive the pre-tax base. Exclusive items keep their
  // marked price as the base. This matches backend /cashier/orders.
  const taxRate = (branchTax.tax_rate || 0) / 100;
  const grossTotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const subtotal = cart.reduce((sum, item) => {
    const line = item.subtotal || 0;
    if (item.price_includes_tax && taxRate > 0) {
      return sum + line / (1 + taxRate);
    }
    return sum + line;
  }, 0);
  const discountAmount = discountType === 'percent' ? subtotal * (discount / 100) : discount;
  const taxableAmount = subtotal - discountAmount;
  const tax = taxableAmount * taxRate;
  const total = taxableAmount + tax;
  const currencySymbol = branchTax.currency_code || 'SAR';

  // iter700 — Live broadcast to the Customer Display window (if open).
  // Uses BroadcastChannel so updates are instant and stay in-browser.
  // We also persist the last snapshot to localStorage so a freshly-
  // opened display window can hydrate immediately on launch.
  useEffect(() => {
    const snapshot = {
      cart, subtotal, tax, total, discount: discountAmount,
      branch: user?.branch_name || '',
      status: cart.length > 0 ? 'active' : 'idle',
      ts: Date.now(),
    };
    try {
      localStorage.setItem('cd_state_v1', JSON.stringify(snapshot));
      if ('BroadcastChannel' in window) {
        const bc = new BroadcastChannel('smarto_customer_display');
        bc.postMessage(snapshot);
        bc.close();
      }
    } catch { /* private mode / quota — graceful skip */ }
  }, [cart, subtotal, tax, total, discountAmount, user?.branch_name]);

  // iter702 — Broadcast the active promo catalogue to the Customer
  // Display once at mount + every ~5 min so newly-saved slides go live
  // without a page reload on the kiosk monitor. The display itself is
  // anonymous (no token), so we MUST proxy this data through the
  // authenticated POS session — that's why it lives here, not on the
  // /customer-display page.
  useEffect(() => {
    let alive = true;
    let intervalId = null;
    const pushPromos = async () => {
      try {
        const branchId = user?.branch_id;
        const { data } = await api.get('/customer-display/promos', {
          params: { active_only: true, ...(branchId ? { branch_id: branchId } : {}) },
        });
        if (!alive) return;
        const promos = Array.isArray(data?.items) ? data.items : [];
        const payload = { type: 'promos', promos };
        try { localStorage.setItem('cd_promos_v1', JSON.stringify(promos)); } catch { /* ignored */ }
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('smarto_customer_display');
          bc.postMessage(payload);
          bc.close();
        }
      } catch { /* the rotator falls back to the static idle screen */ }
    };
    pushPromos();
    intervalId = setInterval(pushPromos, 5 * 60 * 1000);
    return () => { alive = false; if (intervalId) clearInterval(intervalId); };
  }, [user?.branch_id]);

  // iter703 — Fetch upsell suggestions whenever the cart contents
  // change, and broadcast them to the Customer Display so the
  // customer-facing screen can show its own "Add fries · +5 SAR?"
  // overlay. Debounced 300 ms to coalesce rapid +/- taps on the same
  // item.
  useEffect(() => {
    let alive = true;
    const itemIds = cart.map((c) => c.item_id).filter(Boolean);
    if (itemIds.length === 0) {
      setUpsellSuggestions([]);
      if ('BroadcastChannel' in window) {
        try {
          const bc = new BroadcastChannel('smarto_customer_display');
          bc.postMessage({ type: 'upsells', upsells: [] });
          bc.close();
        } catch { /* ignored */ }
      }
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/upsells/suggestions', {
          item_ids: itemIds,
          ...(user?.branch_id ? { branch_id: user.branch_id } : {}),
        });
        if (!alive) return;
        const sugg = Array.isArray(data?.suggestions) ? data.suggestions : [];
        setUpsellSuggestions(sugg);
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('smarto_customer_display');
          bc.postMessage({ type: 'upsells', upsells: sugg });
          bc.close();
        }
      } catch { /* upsells are an optional, non-blocking layer */ }
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cart.map((c) => c.item_id)), user?.branch_id]);

  // Add item to cart
  const handleAddItem = (item) => {
    // Branch-aware sizes/add-ons + per-size branch price via shared helper
    // (same resolution the Waiter uses). Pricing semantics unchanged.
    const dedupedModifiers = resolveBranchModifiers(item, posBranchId);

    if (dedupedModifiers.length > 0) {
      setSelectedItem({ ...item, _filteredModifiers: dedupedModifiers });
      setSelectedModifiers({});
      setShowModifiers(true);
    } else {
      addToCart(item, []);
    }
  };

  const addToCart = (item, modifiers) => {
    const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price || 0), 0);
    // Use branch-specific price if available
    const basePrice = (item.branch_prices && posBranchId && item.branch_prices[posBranchId])
      ? item.branch_prices[posBranchId]
      : item.price;
    const itemTotal = (basePrice + modifierTotal);

    // Below-Cost POS Guard — warn the cashier if this branch's price is set
    // below the item's synced recipe cost.
    if (Number(item.cost_price) > 0 && basePrice < Number(item.cost_price)) {
      toast.warning(`⚠️ Warning: Price is below cost! ${item.name} — SR ${Number(basePrice).toFixed(2)} vs cost SR ${Number(item.cost_price).toFixed(2)}`, { duration: 6000 });
    }
    
    // Check if same item with same modifiers exists
    const existingIndex = cart.findIndex(c => 
      c.item_id === item.id && 
      JSON.stringify(c.modifiers) === JSON.stringify(modifiers)
    );
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].subtotal = newCart[existingIndex].quantity * itemTotal;
      setCart(newCart);
    } else {
      setCart([...cart, {
        item_id: item.id,
        name: item.name,
        name_ar: item.name_ar,
        category: item.category,  // iter657 — needed for smart-routing chip
        unit_price: basePrice,
        modifiers,
        modifier_total: modifierTotal,
        quantity: 1,
        subtotal: itemTotal,
        price_includes_tax: !!item.price_includes_tax,  // iter820 — cart line inherits inclusivity for correct on-screen totals
      }]);
    }
    toast.success(`${item.name} added`);
  };

  const confirmModifiers = () => {
    const modifiers = [];
    for (const [groupName, selection] of Object.entries(selectedModifiers)) {
      if (Array.isArray(selection)) {
        selection.forEach(s => modifiers.push({ group: groupName, ...s }));
      } else if (selection) {
        modifiers.push({ group: groupName, ...selection });
      }
    }
    addToCart(selectedItem, modifiers);
    setShowModifiers(false);
    setSelectedItem(null);
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    } else {
      const itemTotal = newCart[index].unit_price + newCart[index].modifier_total;
      newCart[index].subtotal = newCart[index].quantity * itemTotal;
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  // Handle payment
  // iter740 — Now accepts an optional `splitData` object pushed by the
  // new SplitPaymentDialog: { payment_details: [...], change_due }.
  // When omitted, falls back to the legacy single-method behaviour so
  // older entry points (Quick Pay buttons etc.) keep working.
  // iter845c — handlePayment moved to usePosCheckout hook.

  // iter845b — Printer routing helpers via hook. Owns
  // buildPrintPayload / openOrderInPrintDialog / printOrderViaBridge /
  // printPrepTickets so this page stays focused on cashier lifecycle.
  const { buildPrintPayload, printOrderViaBridge, printPrepTickets } = usePosPrinting({
    printers,
    user,
    branchTax,
    branchInfo,
    branding,
    categories,
    isTrainingActive,
  });

  // Send to kitchen — POSTs to backend, then silently prints a kitchen
  // ticket (KOT) via the local print bridge if a kitchen printer is
  // configured for this branch. Falls back to a toast if bridge or
  // printer is unavailable so the cashier always gets feedback.
  // iter — declared AFTER usePosPrinting so `printPrepTickets` is already
  // initialized (prevents a TDZ "before initialization" render crash).
  const sendToKitchen = async () => {
    // Unpaid cart → fire it now and keep it OPEN (existing hold / save + send-kitchen). Pay later from Open Checks.
    if (cart.length) {
      const checkId = activeCheckId;
      const saved = checkId ? await handleSaveEdit() : await handleHoldBill();
      if (!saved) return;
      const id = checkId || saved.id;
      try {
        const token = localStorage.getItem('cashier_token');
        const { data } = await api.post(`/cashier/orders/${id}/send-kitchen`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(data?.idempotent ? 'Kitchen updated — collect payment from Open Checks' : 'Sent to kitchen — collect payment from Open Checks');
        if (!data?.idempotent && saved.id) await printPrepTickets(saved);
      } catch { toast.error('Failed to send to kitchen'); }
      return;
    }
    if (!lastOrder) { toast.info('No active order to send'); return; }
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      await api.post(`/cashier/orders/${lastOrder.id}/send-kitchen`, {}, { headers });
      toast.success('Order sent to kitchen!');
    } catch (err) {
      toast.error('Failed to send to kitchen');
      return;
    }
    // Silent KOT print via bridge — fans out to all configured prep
    // printers (kitchen lanes + bar) in parallel (iter606).
    await printPrepTickets(lastOrder);
  };

  // iter845c — Full order-creation lifecycle via hook.
  const { handlePayment } = usePosCheckout({
    cart, total, paymentMethod, splitPayments, selectedCustomer,
    discount, discountType, orderType, tableNumber, notes, user, printers,
    scheduledFor, setScheduledFor,
    branchId: posBranchId,
    autoKitchen: branchTax.has_kitchen !== false,
    requestApproval,
    saleMetadata, setSaleMetadata,
    activeCheckId, setActiveCheckId,
    setLastOrder, setCart, setDiscount, setSelectedCustomer, setNotes,
    setTableNumber, setShowPayment, setShowReceipt, setShowCustomerSelect,
    setPrinters, fetchData, setGoGreen,
    buildPrintPayload, printOrderViaBridge, printPrepTickets,
    fetchOrders,
  });

  // iter845i — Online-orders auto-print poller. Every 20s, fetches any
  // orders submitted via /api/public/order that haven't been printed
  // yet, fires the existing print-bridge cascade, then acks the
  // server so it stops appearing. Must be declared AFTER usePosPrinting
  // hook so `printOrderViaBridge` and `printPrepTickets` are in scope.
  const printFnsRef = useRef({ printOrderViaBridge, printPrepTickets });
  printFnsRef.current = { printOrderViaBridge, printPrepTickets };
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const token = localStorage.getItem('cashier_token');
        const { data } = await api.get('/cashier/online-orders/pending-print', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const pending = data?.orders || [];
        for (const o of pending) {
          toast.success(`📥 New online order #${o.order_number} — ${o.customer_name || 'Guest'}`, { duration: 6000 });
          try { printFnsRef.current.printOrderViaBridge(o, { role: 'receipt' }); } catch { /* silent */ }
          try { printFnsRef.current.printPrepTickets(o); } catch { /* silent */ }
          try {
            await api.post(`/cashier/online-orders/${o.id}/print-ack`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch { /* silent */ }
        }
      } catch { /* silent */ }
    };
    const timer = setInterval(tick, 20000);
    tick();
    return () => { cancelled = true; clearInterval(timer); };
  }, []); // stable: print fns read via ref so re-renders don't restart the poller

  // iter620 — End-of-day Z-report. iter621 — supports week / month
  // ranges in addition to the single-day default. `range` is one of:
  //   "today" (default), "week" (last 7 days), "month" (last 30 days)
  const printDayReport = async (range = 'today') => {
    try {
      const token = localStorage.getItem('cashier_token');
      const branchId = user?.branch_id || undefined;
      const today = new Date();
      const fmt = (d) => toLocalYmd(d);
      let params = { branch_id: branchId };
      if (range === 'week') {
        const lo = new Date(today); lo.setDate(today.getDate() - 6);
        params = { ...params, start_date: fmt(lo), end_date: fmt(today) };
      } else if (range === 'month') {
        const lo = new Date(today); lo.setDate(today.getDate() - 29);
        params = { ...params, start_date: fmt(lo), end_date: fmt(today) };
      }
      const { data } = await api.get('/cashier/day-report', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const sym = data.currency || 'SAR';
      const branchName = data.branch?.name || 'Branch';
      const t = data.totals || {};
      const titleMap = { today: 'DAY REPORT', week: '7-DAY REPORT', month: '30-DAY REPORT' };
      const periodLine = data.is_range
        ? `${data.period_start} → ${data.period_end} · ${data.days_covered} days`
        : data.date;

      // iter890 — Route through the shared `printSummary` wrapper — the
      // same 4-tier routing (USB/BT/Sunmi native → TCP → Bridge → iframe)
      // used by order receipts. Previously this function had its own
      // hand-rolled version of that routing (iter888) — collapsing to
      // one code path means future fixes to the print chain automatically
      // benefit Day Report / Z-Report / Shift Close too.
      if (!printers.length) {
        try { await fetchPrinters(); } catch { /* silent */ }
      }
      const summaryPayload = {
        company: branchName,
        title: titleMap[range] || 'REPORT',
        currency: `${sym} `,
        date: periodLine,
        items: [
          ...(data.top_items || []).slice(0, 10).map((it) => ({
            qty: it.qty, name: it.name, subtotal: it.total,
          })),
        ],
        subtotal: t.subtotal,
        tax: t.tax,
        total: t.total,
        footer: `Cash ${sym}${(t.cash || 0).toFixed(2)} · Card ${sym}${((t.bank || 0) + (t.card || 0)).toFixed(2)}\nOrders: ${data.order_count || 0}${data.refund_count ? ` (${data.refund_count} refunded)` : ''}\n${range === 'today' ? 'Z-Report' : range.toUpperCase()} · ${new Date().toLocaleString()}`,
      };
      // iter904 — Width-aware ESC/POS. 58mm rolls have ~32 chars/line;
      // 80mm rolls have ~48. Using a hardcoded 32-dash divider + big
      // company font on 58mm caused overflow/wrap; using them on 80mm
      // left a lot of empty space. Match the receipt formatter's logic.
      //
      // iter910 — Add a 3-char SAFE MARGIN on both sides so the "SAR"
      // (or any 3-char suffix) never gets clipped by the printer's
      // physical dead zone. Effective usable width becomes 26 (58mm)
      // or 42 (80mm) — the divider + right-aligned amounts all size
      // to that inner budget. HTML fallback gets bigger horizontal
      // padding for the same reason.
      const _paperWidth = printers?.find?.(p =>
        (p.type === 'receipt' || !p.type) &&
        (!branchId || p.branch_id === branchId)
      )?.paper_width || '80mm';
      const _isNarrow = _paperWidth === '58mm';
      const _chars = _isNarrow ? 26 : 42;   // iter910 — 3-char pad each side
      const _divider = '-'.repeat(_chars);
      const _companyLine = _isNarrow
        ? `[C]<b>${branchName}</b>`
        : `[C]<b><font size='big'>${branchName}</font></b>`;

      // iter910 — Build the "Items Sold" + "By Category" ESC/POS sections
      // so the physical printout matches the on-screen Day Report. These
      // were previously ONLY in the HTML fallback, so every native / TCP
      // / ePOS / Cloud-relay path skipped them entirely.
      //
      // Right-column price width is currency (up to 4 chars for "SAR ")
      // + up to 9 chars for a number like "1,234.56 " → 13 chars. Truncate
      // the item name to fit inside the remaining budget.
      const _priceCol = 13;
      const _nameCol = Math.max(_chars - _priceCol, 10);
      const _fmt = (n) => Number(n || 0).toFixed(2);
      const _truncName = (s) => {
        const str = String(s || '');
        return str.length > _nameCol ? str.slice(0, _nameCol - 1) + '…' : str;
      };
      const _itemLines = (data.top_items || []).slice(0, 10).map((it) => (
        `[L]${it.qty}× ${_truncName(it.name)}[R]${sym}${_fmt(it.total)}`
      ));
      const _catLines = (data.by_category || []).slice(0, 8).map((c) => (
        `[L]${_truncName(c.name)} (${c.qty})[R]${sym}${_fmt(c.total)}`
      ));

      const escposLines = [
        _companyLine,
        `[C]${titleMap[range] || 'REPORT'}`,
        `[C]${periodLine}`,
        `[C]${_divider}`,
        `[L]Cash[R]${sym}${_fmt(t.cash)}`,
        `[L]Card / Bank[R]${sym}${_fmt((t.bank || 0) + (t.card || 0))}`,
        `[L]<b>TOTAL</b>[R]<b>${sym}${_fmt(t.total)}</b>`,
        `[C]${_divider}`,
        `[L]Subtotal[R]${sym}${_fmt(t.subtotal)}`,
        `[L]VAT[R]${sym}${_fmt(t.tax)}`,
        `[L]Discount[R]-${sym}${_fmt(t.discount)}`,
        `[L]Orders[R]${data.order_count || 0}${data.refund_count ? ` (${data.refund_count} refund)` : ''}`,
        // iter910 — Items sold — the missing sections users flagged
        ...(_catLines.length ? [
          `[C]${_divider}`,
          `[C]<b>By Category</b>`,
          ..._catLines,
        ] : []),
        ...(_itemLines.length ? [
          `[C]${_divider}`,
          `[C]<b>Top Items</b>`,
          ..._itemLines,
        ] : []),
        `[C]${_divider}`,
        `[C]${range === 'today' ? 'Z-Report' : range.toUpperCase()}`,
        `[C]${new Date().toLocaleString()}`,
        '[C] ',
      ];

      const itemsRows = (data.top_items || []).map(
        (it) => `<div style="display:flex;justify-content:space-between;gap:8px">
          <span>${it.qty} × ${it.name}</span><span>${sym} ${Number(it.total).toFixed(2)}</span>
        </div>`
      ).join('');
      const catRows = (data.by_category || []).slice(0, 8).map(
        (c) => `<div style="display:flex;justify-content:space-between;gap:8px;font-size:11px">
          <span>${c.name} (${c.qty})</span><span>${sym} ${Number(c.total).toFixed(2)}</span>
        </div>`
      ).join('');
      const htmlFallback = `<!doctype html><html><head><style>
        /* iter910 — Wider horizontal padding so 3-char labels (SAR, VAT)
           are never clipped by the printer's physical dead zone. */
        @page { size: ${_paperWidth} auto; margin: ${_isNarrow ? '3mm 4mm' : '3mm 6mm'}; }
        body { font-family: monospace; width: ${_isNarrow ? '46mm' : '64mm'}; padding: 4mm ${_isNarrow ? '4mm' : '6mm'}; font-size: ${_isNarrow ? '10px' : '12px'}; margin: 0 auto; }
        h1 { font-size: ${_isNarrow ? '12px' : '14px'}; text-align: center; margin: 2px 0; }
        h2 { font-size: ${_isNarrow ? '9px' : '11px'}; margin: 6px 0 2px; border-bottom: 1px dashed #999; padding-bottom: 1px; text-transform: uppercase; }
        .row { display:flex; justify-content:space-between; gap:8px; }
        .row span:last-child { white-space: nowrap; }   /* keep "SAR 12.34" on one line */
        .strong { font-weight: bold; }
        hr { border: none; border-top: 1px dashed #999; margin: 4px 0; }
        .center { text-align: center; }
      </style></head><body>
        <h1>${titleMap[range] || 'REPORT'}</h1>
        <div class="center">${branchName}</div>
        <div class="center" style="font-size:10px;color:#666">${periodLine}</div>
        <hr/>
        <h2>Sales by payment</h2>
        <div class="row"><span>Cash</span><span>${sym} ${t.cash?.toFixed(2) || '0.00'}</span></div>
        <div class="row"><span>Bank / Card</span><span>${sym} ${(t.bank + t.card).toFixed(2)}</span></div>
        <div class="row strong"><span>TOTAL</span><span>${sym} ${t.total?.toFixed(2) || '0.00'}</span></div>
        <hr/>
        <h2>Breakdown</h2>
        <div class="row"><span>Subtotal</span><span>${sym} ${t.subtotal?.toFixed(2) || '0.00'}</span></div>
        <div class="row"><span>VAT</span><span>${sym} ${t.tax?.toFixed(2) || '0.00'}</span></div>
        <div class="row"><span>Discount</span><span>- ${sym} ${t.discount?.toFixed(2) || '0.00'}</span></div>
        <div class="row"><span>Orders</span><span>${data.order_count}${data.refund_count ? ` (${data.refund_count} refunded)` : ''}</span></div>
        ${data.is_range ? `<div class="row"><span>Avg / day</span><span>${sym} ${(t.total / Math.max(data.days_covered, 1)).toFixed(2)}</span></div>` : ''}
        <hr/>
        ${catRows ? `<h2>By Category</h2>${catRows}<hr/>` : ''}
        ${itemsRows ? `<h2>Top Items</h2>${itemsRows}<hr/>` : ''}
        <div class="center" style="font-size:10px;color:#666">${range === 'today' ? 'Z-Report' : range.toUpperCase()} · ${new Date().toLocaleString()}</div>
        <script>window.onload=()=>{setTimeout(()=>{window.print();},150)}</script>
      </body></html>`;

      const { printSummary } = await import('@/lib/printSummary');
      const result = await printSummary({
        printers,
        branchId,
        title: titleMap[range] || 'Day report',
        payload: summaryPayload,
        escposLines,
        htmlFallback,
      });
      if (result?.mode === 'iframe') {
        toast.success(`${titleMap[range] || 'Report'} for ${periodLine} — ${data.order_count} orders · ${sym} ${t.total?.toFixed(2) || '0.00'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build report');
    }
  };

  // iter845b — Printer routing extracted to usePosPrinting hook.

  // Print receipt — tries bridge first (silent), opens the preview
  // dialog as a fallback when no bridge / no printer is configured.
  const handlePrintReceipt = async () => {
    if (!lastOrder) { toast.info('No recent order to reprint'); return; }
    // Always refresh printers list before deciding so we pick up new ones.
    if (!printers.length) await fetchPrinters();
    const ok = await printOrderViaBridge(lastOrder, { role: 'receipt' });
    if (!ok) {
      // Fall back to the browser preview/iframe dialog.
      setReceiptOrderId(lastOrder.id);
    }
  };

  // iter746 — Resend a previous bill via WhatsApp / Email from Order
  // History. Tries the order's linked customer contact first; if none,
  // prompts the cashier for the number/email and retries.
  const handleResendWhatsApp = async (order) => {
    if (!order) return;
    const token = localStorage.getItem('cashier_token');
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const send = async (phone) => {
      const tId = toast.loading('Sending on WhatsApp…');
      await api.post(`/cashier/orders/${order.id}/send-whatsapp`, phone ? { phone } : {}, h);
      toast.success('Bill resent on WhatsApp ✓', { id: tId });
    };
    try {
      await send('');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /phone|number/i.test(detail)) {
        const phone = window.prompt('Enter WhatsApp number (e.g. +966501234567):', '');
        if (!phone) return;
        try { await send(phone.trim()); }
        catch (e2) { toast.error(e2.response?.data?.detail || 'WhatsApp send failed'); }
      } else {
        toast.error(detail || 'WhatsApp send failed');
      }
    }
  };

  const handleResendEmail = async (order) => {
    if (!order) return;
    const token = localStorage.getItem('cashier_token');
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const send = async (email) => {
      const tId = toast.loading('Emailing bill…');
      await api.post(`/cashier/orders/${order.id}/send-email`, email ? { email } : {}, h);
      toast.success('Bill resent by email ✓', { id: tId });
    };
    try {
      await send('');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /email/i.test(detail)) {
        const email = window.prompt('Enter customer email:', '');
        if (!email) return;
        try { await send(email.trim()); }
        catch (e2) { toast.error(e2.response?.data?.detail || 'Email send failed'); }
      } else {
        toast.error(detail || 'Email send failed');
      }
    }
  };

  // iter814 — Download the ZATCA-stamped tax invoice PDF (with cleared QR).
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const handleDownloadInvoice = async (order) => {
    if (!order) return;
    setDownloadingInvoice(order.id);
    const tId = toast.loading('Preparing tax invoice…');
    try {
      const token = localStorage.getItem('cashier_token');
      const res = await api.get(`/zatca-p2/invoice-pdf/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      // iter945 — Reuse the existing PDF blob. On the Android/iOS Capacitor
      // shell the browser <a download> + blob: URL is silently dropped, so
      // saveOrShareBlob writes it via Filesystem + OS share sheet; on the
      // web it keeps the exact same browser download behaviour.
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `tax-invoice-${order.order_number || order.id.slice(0, 8)}.pdf`;
      const out = await saveOrShareBlob(blob, filename);
      toast.success(out.mode === 'native-share' ? 'Tax invoice ready to save/share' : 'Tax invoice downloaded', { id: tId });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the invoice', { id: tId });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // iter804 — Retry a failed/pending ZATCA submission from Order History.
  const [retryingZatca, setRetryingZatca] = useState(null);
  const handleRetryZatca = async (order) => {
    if (!order) return;
    setRetryingZatca(order.id);
    const tId = toast.loading('Submitting to ZATCA…');
    try {
      const token = localStorage.getItem('cashier_token');
      const { data } = await api.post(`/zatca-p2/report-order/${order.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.message || 'Submitted to ZATCA ✓', { id: tId });
      // Reflect new status locally + refresh the list.
      if (data.zatca) {
        setViewingOrder((prev) => (prev && prev.id === order.id ? { ...prev, zatca: data.zatca } : prev));
      }
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'ZATCA submission failed', { id: tId });
    } finally {
      setRetryingZatca(null);
    }
  };


  /**
   * iter666 — Hand the receipt PDF to Epson iPrint via the iOS Share
   * Sheet. Native iOS app → talks directly to TM-m30II over TCP-9100,
   * bypassing AirPrint discovery entirely. Use case: iPad-only branches
   * where AirPrint can't see the printer + no PC/Android Bridge running.
   *
   * Cashier flow: tap "Send to Epson iPrint" → iOS share sheet opens →
   * tap "iPrint" → iPrint app opens with the PDF → tap "Print".
   */
  const handleSendIPrint = async () => {
    if (!lastOrder) { toast.info('No recent order to share'); return; }
    if (!printers.length) await fetchPrinters();
    const receiptPrinter = printers.find((p) => (p.type || 'receipt') === 'receipt') || printers[0] || { paper_width: '80mm' };
    const payload = buildPrintPayload(lastOrder, { role: 'receipt' });
    toast.info('Preparing receipt PDF…');
    const r = await printViaIPrintShare(receiptPrinter, payload);
    if (r.ok) {
      toast.success('Pick "iPrint" from the share sheet');
    } else if (r.error === 'cancelled') {
      // Silent — user dismissed the sheet on purpose.
    } else if (r.error === 'web-share-unavailable' || r.error === 'cannot-share-file') {
      toast.error('Share Sheet not available on this browser. Use Safari on iPad.');
    } else {
      toast.error(`Could not share receipt: ${r.error}`);
    }
  };

  // Logout
  const doLogout = () => {
    localStorage.removeItem('cashier_token');
    localStorage.removeItem('cashier_user');
    navigate('/cashier');
  };
  const handleLogout = () => {
    // End-of-shift close-out: review/send today's totals before logging off.
    setCloseoutMode(true);
    setShowDaySummary(true);
  };

  const [showMobilePosCart, setShowMobilePosCart] = useState(false);

  // Touch-Mode (iter378) — categories grid → drill into items.
  // 'categories' shows the visual category tiles (matches reference UX).
  // 'items' shows the items inside the chosen category with a Back button.
  // Typing in the search jumps to 'items' across all categories.
  const [posView, setPosView] = useState('categories');
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showDiscountSheet, setShowDiscountSheet] = useState(false);

  // Open category tile → drill into items grid.
  const handleOpenCategory = (catId) => {
    setSelectedCategory(catId);
    setPosView('items');
  };
  const handleBackToCategories = () => {
    setPosView('categories');
    setSelectedCategory('all');
    setSearchQuery('');
  };
  // Searching auto-switches to items view so results are visible.
  useEffect(() => {
    if (searchQuery && posView === 'categories') setPosView('items');
  }, [searchQuery, posView]);

  // iter893 — Reset the order-type-chooser gate the moment the cart
  // empties AND we're not in the middle of editing an existing order.
  // This runs after every completed order (usePosCheckout clears the
  // cart on success) so the next invoice must start with an explicit
  // Dine-in / Takeaway / Delivery pick.
  useEffect(() => {
    if (cart.length === 0 && !editingOrder) {
      setOrderTypeChosen(false);
    }
  }, [cart.length, editingOrder]);

  // iter893 — Reprint the most recent order with a single tap. Uses
  // the already-loaded `lastOrder` when present (the just-completed
  // order); otherwise pulls the newest row from the cashier order
  // list. Routes through the shared print pipeline so USB / BT /
  // Sunmi tablets keep working.
  // iter895 — Extended with a "pick from last 10" popover so cashiers
  // can reprint ANY recent order, not just the very last one. The
  // one-tap fast-path is preserved when there IS a `lastOrder` in
  // memory (the just-completed order), so the common case stays
  // instant. The popover only kicks in when the cashier explicitly
  // opens it or when there's no in-memory last order.
  const [reprintPickerOpen, setReprintPickerOpen] = useState(false);
  const [reprintCandidates, setReprintCandidates] = useState([]);
  const [reprintLoading, setReprintLoading] = useState(false);

  const doReprint = useCallback(async (target) => {
    if (!target) { toast.info('No previous invoice to reprint'); return; }
    toast.loading(`Reprinting #${target.order_number || (target.id||'').slice(0,8)}…`, { id: 'reprint-last' });
    try { await printOrderViaBridge(target, { role: 'receipt' }); }
    catch { /* soft-fall through to success toast; bridge handles errors */ }
    toast.success(`Reprint sent → #${target.order_number || (target.id||'').slice(0,8)}`, { id: 'reprint-last' });
  }, [printOrderViaBridge]);

  const openReprintPicker = useCallback(async () => {
    setReprintPickerOpen(true);
    setReprintLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const { data } = await api.get('/cashier/orders?limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(data) ? data : (data?.orders || data?.items || []);
      setReprintCandidates(list.slice(0, 10));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load recent orders');
      setReprintCandidates([]);
    } finally {
      setReprintLoading(false);
    }
  }, []);

  const handleReprintLastInvoice = useCallback(async () => {
    // iter895 — Always open the picker so cashiers can pick ANY of
    // the last 10 orders. The picker pre-highlights the newest order
    // with a "Latest" badge and a big "Reprint Latest" one-tap button
    // at the top, so the common case is still fast.
    await openReprintPicker();
  }, [openReprintPicker]);


  // Void = clear cart with a confirm guard (Foodics-parity action).
  const handleVoidCart = () => {
    if (cart.length === 0) { toast.info('Cart is already empty'); return; }
    if (!window.confirm('Void this order? All items will be removed.')) return;
    setCart([]); setDiscount(0); setNotes(''); setSelectedCustomer(null);
    toast.success('Order voided');
  };

  if (!user) return null;

  return (
    <>
    {/* iter648 — iPad-friendly print toast (polls history endpoint
        every 5 s and toasts when a job completes / fails). */}
    <PrintJobToaster enabled />
    {/* iter601 — keep POS manifest active so PWA launch from iPad
        home-screen still scopes to /cashier, even if the user
        bookmarked /cashier/pos directly. */}
    <POSManifestSwapper />
    {/* iter599 — Kiosk-mode "Exit" pill. Tiny, top-right, only visible
        when ?kiosk=1 is active. The button is the only sanctioned way
        out (back-button is trapped, pop-up close may not be available
        on tablets that opened in the same tab). */}
    {kioskMode && <KioskExitPill onExit={exitKioskMode} />}
    {/* iter616 — Training-mode banner. Visible at the top whenever the
        branch is in training. Forces every print to OS dialog so new
        staff see what would print before paper moves. Tap to disable. */}
    {isTrainingActive && (
      <TrainingModeBanner
        onDisable={async () => {
          try {
            const token = localStorage.getItem('cashier_token');
            await api.post(`/branches/${user?.branch_id}/training-mode`, { minutes: 0 }, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setTrainingUntil(null);
            toast.success('Training mode disabled — back to normal printing.');
          } catch { toast.error('Failed to disable training mode.'); }
        }}
      />
    )}
    <div className="h-screen flex flex-col lg:flex-row bg-[#f5f5f5] overflow-hidden" data-testid="cashier-pos" data-kiosk={kioskMode ? '1' : undefined}>
      {/* Left: Menu Section */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header - Foodics style: clean white, no heavy borders.
            iter822 — wrap on cramped tablets (7–10") instead of forcing
            a horizontal scroll; header items become two rows if needed. */}
        <div className="bg-white shadow-sm p-3 sm:p-4 flex flex-wrap items-center justify-between gap-x-2 gap-y-2" data-testid="pos-header">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
              <UtensilsCrossed size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-sm sm:text-base font-outfit text-foreground" data-testid="pos-title">Restaurant POS</h1>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Building2 size={11} />
                <span className="font-medium text-primary truncate">{user.branch_name || 'Main Branch'}</span>
                {/* iter689 — "Away from home" indicator. Visible only
                    when the terminal branch differs from the cashier's
                    home branch (cashier shift-swapped to another
                    branch). Helps the cashier instantly notice if
                    they're at the wrong terminal before ringing up
                    sales — and gives reports auditors a visual cue. */}
                {user.home_branch_id && user.branch_id && user.home_branch_id !== user.branch_id && (
                  <span
                    className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[9px] font-semibold"
                    title={`Operating away from your home branch. Sales today are recorded under ${user.branch_name}.`}
                    data-testid="pos-away-from-home-chip"
                  >
                    away
                  </span>
                )}
                <ZatcaStatusDot className="ml-1" showLabel interactive />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto min-w-0 [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {/* Help button — opens the 4-step "First Sale" tour for new
                cashiers/managers without stealing screen real-estate
                that the cart needs at the bottom. */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowTour(true)}
              className="h-10 w-10 p-0 rounded-xl text-muted-foreground hover:text-violet-600 hover:bg-violet-50"
              title="How does the POS work? (4-step tour)"
              data-testid="pos-help-btn"
            >
              <HelpCircle size={16} />
            </Button>
            <TodayTile branchId={user?.branch_id} cashierToken={localStorage.getItem('cashier_token')} refreshKey={lastOrder?.id} fallback={stats} currencySymbol={currencySymbol} />
            {/* iter957 — Branch picker: sets which branch this till acts as. */}
            <POSBranchPicker
              userRole={user?.role || 'cashier'}
              homeBranchId={user?.branch_id || null}
              value={activeBranchId}
              onChange={setActiveBranchId}
            />
            <StockRequestButton myBranchId={activeBranchId || user?.branch_id} myBranchName={user?.branch_name || 'Main Branch'} size="sm" variant="outline" label="Stock" className="h-10 rounded-xl text-xs hidden sm:inline-flex" />
            <PosSettingsButton
              density={density}
              onToggleDensity={toggleDensity}
              kitchenPrinting={kitchenPrinting}
              onToggleKitchenPrinting={toggleKitchenPrinting}
              autoPrint={autoPrintOnPay}
              onToggleAutoPrint={toggleAutoPrintOnPay}
              onAddExpense={() => navigate('/scan-invoice')}
              toolbarHidden={actionHidden}
              onToggleToolbarButton={toggleToolbarButton}
              canEditToolbar={canEditToolbar}
            />
            <Button size="sm" className="lg:hidden bg-primary hover:bg-primary/90 relative h-10 w-10 p-0 rounded-xl" onClick={() => setShowMobilePosCart(true)} data-testid="pos-mobile-cart-toggle">
              <ShoppingCart size={16} />
              {cart.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full text-[9px] flex items-center justify-center text-white">{cart.length}</span>}
            </Button>
            {/* iter677 — Printer health badge: live status of every IP
                printer at this branch. Only renders on iPad/Samsung Tab. */}
            <PrinterHealthBadge branchId={user?.branch_id} />
            {/* iter845i — Cashier quick-toggle: pause the customer-facing
                online ordering during a rush. Auto-hides when the branch
                has never enabled online ordering. */}
            <PauseOnlineOrdersChip branchId={user?.branch_id} />
            {/* iter710 — Persistent reminder pill when ePOS debug toasts
                are enabled. Cashiers occasionally see the "[debug] …
                fetch OK" toast and think it's an error — this pill makes
                the source obvious and lets them disable it with one tap.
                Hidden in production unless explicitly turned on. */}
            <EposDebugPill />
            {/* iter698 — Action chips moved into <POSToolbarActions> so
                cashiers/managers can customise which chips are inline vs
                in the ⋯ overflow vs hidden. Per-device (localStorage)
                so we don't risk any live transaction or report data. */}
            <POSToolbarActions
              userRole={user?.role || 'cashier'}
              countryCode={branding?.country_code || ''}
              currentShift={currentShift}
              branchId={activeBranchId || user?.branch_id || null}
              onChangeOrderType={() => { setOrderTypeChosen(false); toast.info('Pick the order type again'); }}
              onOpenOrders={() => { setShowOrderHistory(true); fetchOrders(); }}
              onOpenPrinters={() => { setShowPrinterSettings(true); fetchPrinters(); }}
              onOpenDrawer={openCashDrawer}
              onOpenShiftModal={() => setShowShiftModal(true)}
              onPrintDayReport={printDayReport}
              onOpenDaySummary={() => setShowDaySummary(true)}
              onOpenOpenChecks={() => setShowOpenChecks(true)}
              openChecksCount={openChecksCount}
              capabilities={{ reconcile: canReconcile, tables: branchTax.has_tables !== false, kitchen: branchTax.has_kitchen !== false }}
              onOpenReconcile={() => setShowReconcile(true)}
              // iter699 — Compliance / accessibility chips. Open existing
              // pages where they exist; show "not configured" toasts as
              // graceful fallbacks otherwise. Keeps the operator's day
              // moving — they can always wire up a real ZATCA flow later.
              onOpenZatcaStatus={() => {
                // Existing page — already protected to manager+ at the router level.
                // iter945 — Android WebView can't open a new tab, so navigate
                // in-app on the native shell; keep new-tab behaviour on web.
                if (isCapacitorNative()) { navigate('/zatca-phase2'); return; }
                window.open('/zatca-phase2', '_blank', 'noopener,noreferrer');
              }}
              onOpenFiscalStatus={async () => {
                // iter700 — Ping the backend stub. Today it always says
                // "ok: true, device: stub". When you provision a real
                // SDC (Italian RT, KSA Phase 2 SDK, etc.), wire it in
                // backend/routers/pos_settings.py::fiscal_heartbeat and
                // this chip becomes a real device-health indicator.
                try {
                  const { data } = await api.get('/fiscal/heartbeat', { params: user?.branch_id ? { branch_id: user.branch_id } : {} });
                  if (data?.ok) {
                    toast.success(
                      `Fiscal device: ${data.device || 'reachable'}${data.last_seen ? ' · last seen ' + new Date(data.last_seen).toLocaleTimeString() : ''}`,
                      { duration: 5000 }
                    );
                  } else {
                    toast.error('Fiscal device unreachable.');
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Fiscal heartbeat call failed.');
                }
              }}
              onOpenCustomerDisplay={() => {
                // iter945 — Android WebView can't open a second window. On the
                // native shell, open the existing /customer-display route in-app
                // so the button still works; on web keep the pop-out window so
                // the cashier can drag it to a secondary monitor.
                if (isCapacitorNative()) { navigate('/customer-display'); return; }
                const win = window.open('/customer-display', 'smarto_customer_display', 'noopener,noreferrer,width=1024,height=600');
                if (!win) toast.error('Browser blocked the pop-up. Allow pop-ups for Smarto then retry.');
                else toast.success('Customer Display opened — drag it to your second screen.');
              }}
              onScanBill={() => navigate('/scan-invoice')}
            />
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 rounded-xl text-xs font-medium text-muted-foreground">
              <User size={13} />{user.name}
            </div>
            <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="h-10 w-10 p-0 rounded-xl text-muted-foreground hover:text-error">
              <LogOut size={16} />
            </Button>
          </div>
        </div>

        {/* iter830 — Live menu-updated banner. Shown when the
            /cashier/menu-version probe detects an out-of-band change
            (manager tweaked a price/VAT flag in another tab). One tap
            refetches menu/categories/tax so the cart calc is
            immediately based on the new data. */}
        {menuChanged && <MenuUpdatedBanner onRefresh={acceptMenuUpdate} />}

        {/* Touch-Mode action toolbar (iter378) — Foodics-parity row of
            cashier actions. Extracted iter845. */}
        <PosActionToolbar
          onPrintReceipt={handlePrintReceipt}
          onSendKitchen={sendToKitchen}
          onVoidCart={handleVoidCart}
          onOpenDiscount={() => setShowDiscountSheet(true)}
          onOpenNotes={() => setShowNotesDialog(true)}
          onOpenPopular={() => { setSelectedCategory('popular'); setPosView('items'); }}
          onReprintLast={handleReprintLastInvoice}
          hiddenKeys={actionHidden}
        />

        {/* Search + Back-to-categories crumb */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2">
            {branchTax.business_type === 'tailor' && (
              <Button
                size="sm"
                variant="outline"
                className={`h-10 rounded-xl shrink-0 ${saleMetadata?.measurements && Object.keys(saleMetadata.measurements).length > 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300' : 'border-border bg-white'}`}
                onClick={() => setShowMeasurements(true)}
                data-testid="pos-measurements-btn"
                title="Capture customer measurements for this sale"
              >
                <Ruler size={16} className="mr-1" />Measurements{saleMetadata?.measurements && Object.keys(saleMetadata.measurements).length > 0 ? ' ✓' : ''}
              </Button>
            )}
            {posView === 'items' && !searchQuery && (
              <Button
                size="sm"
                variant="outline"
                className="h-10 rounded-xl border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 dark:bg-violet-950/30 dark:border-violet-800 dark:text-violet-300 shrink-0"
                onClick={handleBackToCategories}
                data-testid="pos-back-to-categories"
              >
                <ChevronLeft size={16} className="mr-1" />Back
              </Button>
            )}
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-3 text-muted-foreground" />
              <Input
                placeholder={posView === 'categories' ? tLang('pos_search_categories') : tLang('pos_search_items')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 rounded-xl border-border bg-white shadow-sm focus:shadow-md transition-shadow"
                data-testid="menu-search"
              />
            </div>
          </div>
        </div>

        {/* Categories grid (touch-mode) — extracted iter845. */}
        {posView === 'categories' && (
          <PosCategoryGrid categories={categories} onOpenCategory={handleOpenCategory} />
        )}

        {/* iter — Persistent category tabs above the item grid so the cashier
            can switch categories without pressing Back. Reuses the existing
            `categories` data + `selectedCategory` filter and the existing item
            grid. Hidden while searching (search already spans all items). */}
        {posView === 'items' && !searchQuery && categories.length > 0 && (
          <div className="px-4 pt-2">
            <div
              className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="pos-category-tabs"
            >
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-semibold border-2 whitespace-nowrap transition-colors ${selectedCategory === 'all' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-foreground border-border hover:bg-muted'}`}
                data-testid="pos-category-tab-all"
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-semibold border-2 whitespace-nowrap transition-colors ${selectedCategory === cat.id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-foreground border-border hover:bg-muted'}`}
                  data-testid={`pos-category-tab-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Items grid — extracted iter845. */}
        {posView === 'items' && (
          <PosItemsGrid
            filteredItems={filteredItems}
            taxRate={taxRate}
            categories={categories}
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            currencySymbol={currencySymbol}
            isComfortable={isComfortable}
            isItemScheduleActive={isItemScheduleActive}
            onAddItem={handleAddItem}
          />
        )}
      </div>

      {/* Right: Cart Section — extracted iter845 into <PosCart>. */}
      <PosCart
        onHoldBill={() => handleHoldBill()}
        showMobilePosCart={showMobilePosCart}
        setShowMobilePosCart={setShowMobilePosCart}
        cart={cart}
        editingOrder={editingOrder}
        onCancelEdit={() => { setEditingOrder(null); setCart([]); setDiscount(0); setNotes(''); }}
        orderType={orderType}
        setOrderType={setOrderType}
        goGreen={goGreen}
        onToggleGoGreen={() => setGoGreen((v) => !v)}
        onStaffFood={() => (cart.length ? setShowStaffFood(true) : toast.info('Add items to the cart first'))}
        tableNumber={tableNumber}
        setTableNumber={setTableNumber}
        scheduledFor={scheduledFor}
        setScheduledFor={setScheduledFor}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        subtotal={subtotal}
        discountAmount={discountAmount}
        tax={tax}
        total={total}
        currencySymbol={currencySymbol}
        branchTax={branchTax}
        discount={discount}
        setDiscount={setDiscount}
        discountType={discountType}
        setDiscountType={setDiscountType}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        setShowPayment={setShowPayment}
        setShowCustomerSelect={setShowCustomerSelect}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        onSaveEdit={() => handleSaveEdit()}
        upsellSuggestions={upsellSuggestions}
        menuItems={menuItems}
        handleAddItem={handleAddItem}
        categories={categories}
        printers={printers}
        user={user}
        branchName={branchInfo?.name}
      />

      {/* Mobile floating cart button */}
      {cart.length > 0 && !showMobilePosCart && (
        <MobileCartFAB
          count={cart.length}
          total={total}
          currencySymbol={currencySymbol}
          onOpen={() => setShowMobilePosCart(true)}
        />
      )}


      <ModifiersDialog
        open={showModifiers}
        onOpenChange={setShowModifiers}
        selectedItem={selectedItem}
        selectedModifiers={selectedModifiers}
        setSelectedModifiers={setSelectedModifiers}
        confirmModifiers={confirmModifiers}
        currencySymbol={currencySymbol}
      />

      {/* iter740 — Foodics-style split-payment dialog. The cashier
          can split one bill across Cash + Bank + Card etc. and pick
          which bank account received the bank portion. The legacy
          PaymentDialog is kept for any single-method shortcuts. */}
      <SplitPaymentDialog
        open={showPayment}
        onOpenChange={setShowPayment}
        initialBillMethod={goGreen ? 'go_green' : 'print'}
        total={total}
        subtotal={subtotal}
        discountAmount={discountAmount}
        tax={tax}
        branchTax={branchTax}
        currencySymbol={currencySymbol}
        notes={notes}
        setNotes={setNotes}
        billOptions={billOptions}
        defaultContact={{ phone: selectedCustomer?.phone || '', email: selectedCustomer?.email || '' }}
        branchId={posBranchId || user?.branch_id || 'default'}
        initialMethod={paymentMethod}
        onConfirm={(splitData) => {
          setShowPayment(false);
          handlePayment(splitData);
        }}
      />

      <CustomerSelectDialog
        open={showCustomerSelect}
        onOpenChange={setShowCustomerSelect}
        customers={customers}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        onContinue={() => { setShowCustomerSelect(false); setShowPayment(true); }}
      />

      {/* iter893 — Mandatory order-type chooser on every fresh invoice.
          Only shows when the cart is empty AND the operator hasn't
          already picked for this invoice. Prevents the "did we ring
          this up as dine-in or takeaway?" ambiguity at close-of-day. */}
      <OrderTypeChooserDialog
        open={!orderTypeChosen && cart.length > 0 && !editingOrder && !showShiftModal && !showTour && (branchTax.order_types?.length ?? 3) > 1}
        orderTypes={branchTax.order_types}
        onChoose={(type) => {
          setOrderType(type);
          setOrderTypeChosen(true);
          // Keep a table number typed before the first item (Dine In); clear it for other types.
          if (type !== 'dine_in') setTableNumber('');
          toast.success(
            type === 'dine_in' ? 'Dine-In · locked for this invoice' :
            type === 'takeaway' ? 'Takeaway · locked for this invoice' :
            type === 'delivery' ? 'Delivery · locked for this invoice' :
            `${type.replace('_', ' ')} · locked for this invoice`,
            { duration: 2000 },
          );
        }}
      />

      <OrderCompleteDialog
        open={showReceipt}
        onOpenChange={setShowReceipt}
        lastOrder={lastOrder}
        currencySymbol={currencySymbol}
        onPrintReceipt={handlePrintReceipt}
        onSendKitchen={sendToKitchen}
        onSendIPrint={handleSendIPrint}
        onVoidOrder={lastOrder ? () => setShowVoidOrder(true) : undefined}
      />

      <StaffFoodDialog
        open={showStaffFood}
        onOpenChange={setShowStaffFood}
        cart={cart}
        branchId={posBranchId}
        onSubmitted={() => { setCart([]); setDiscount(0); setNotes(''); setGoGreen(false); }}
      />

      <VoidLastOrderDialog
        open={showVoidOrder}
        onOpenChange={setShowVoidOrder}
        order={lastOrder}
        onVoided={() => {
          setShowVoidOrder(false);
          setShowReceipt(false);
          fetchData();
        }}
      />

      {/* iter895 — Reprint any of the last 10 orders. */}
      <ReprintPickerDialog
        open={reprintPickerOpen}
        onOpenChange={setReprintPickerOpen}
        orders={reprintCandidates}
        loading={reprintLoading}
        currency={currencySymbol}
        onReprint={async (o) => {
          setReprintPickerOpen(false);
          await doReprint(o);
        }}
      />

      {/* Shift Management Modal */}
      <CashierShiftModal
        open={showShiftModal}
        onClose={() => setShowShiftModal(false)}
        onShiftChange={(shift) => {
          setCurrentShift(shift);
          fetchData();
        }}
      />

      <OrderHistoryDialog
        open={showOrderHistory}
        onOpenChange={setShowOrderHistory}
        orders={orders}
        loadingOrders={loadingOrders}
        viewingOrder={viewingOrder}
        setViewingOrder={setViewingOrder}
        onEditOrder={guardSwitch(handleEditOrder)}
        onDeleteOrder={handleDeleteOrder}
        onRefundOrder={setRefundOrder}
        canRefund={canRefund}
        onPrintReceipt={(id) => setReceiptOrderId(id)}
        onResendWhatsApp={handleResendWhatsApp}
        onResendEmail={handleResendEmail}
        onRetryZatca={handleRetryZatca}
        retryingZatca={retryingZatca}
        onDownloadInvoice={handleDownloadInvoice}
        downloadingInvoice={downloadingInvoice}
        billOptions={billOptions}
        branchTax={branchTax}
        currencySymbol={currencySymbol}
      />

      <RefundOrderDialog
        open={!!refundOrder}
        onOpenChange={(o) => !o && setRefundOrder(null)}
        order={refundOrder}
        currencySymbol={currencySymbol}
        onRefunded={() => { setViewingOrder(null); fetchOrders(); }}
      />

      <PrinterSettingsDialog
        open={showPrinterSettings}
        onOpenChange={setShowPrinterSettings}
        printers={printers}
        printerForm={printerForm}
        setPrinterForm={setPrinterForm}
        editingPrinter={editingPrinter}
        setEditingPrinter={setEditingPrinter}
        savePrinter={savePrinter}
        deletePrinter={deletePrinter}
        testPrinter={testPrinter}
      />

      <DayReportDialog
        open={showDaySummary}
        onOpenChange={(v) => { setShowDaySummary(v); if (!v) setCloseoutMode(false); }}
        branchId={posBranchId}
        onPrint={printDayReport}
        closeout={closeoutMode}
        onLogout={doLogout}
      />

      {/* Waiter/Table — Cashier Open Checks (active checks for this branch). */}
      <OpenChecksDialog
        open={showOpenChecks}
        onOpenChange={setShowOpenChecks}
        branchId={posBranchId}
        onOpenCheck={guardSwitch(handleOpenCheck)}
        onVoidCheck={(c) => handleDeleteOrder(c.id, true)}
        refreshKey={openChecksRefresh}
      />
      <ManagerApprovalDialog request={approvalReq} onClose={() => setApprovalReq(null)} />
      <UnsavedCartDialog
        open={!!pendingSwitch}
        saving={switchSaving}
        onSave={handleSwitchSave}
        onDiscard={() => { const p = pendingSwitch; setPendingSwitch(null); p.load(p.order); }}
        onStay={() => setPendingSwitch(null)}
      />

      {/* Phase B — POS Sales Reconciliation. Reuses the shared dialog and the
          existing GET/POST /sales engine (VAT/journals/receivable/realtime all
          reused). Preselects the active POS branch; cash/bank/credit as-is. */}
      <SalesReconciliationDialog
        open={showReconcile}
        onOpenChange={setShowReconcile}
        branches={posBranchId ? [{ id: posBranchId, name: branchInfo?.name || user?.branch_name || 'This branch' }] : []}
        defaultBranchId={posBranchId || ''}
        canRecord={reconcileAccess.record}
        onPosted={() => { fetchOrders(); }}
      />
      {/* Notes dialog (iter378) — extracted iter845. */}
      <PosNotesDialog
        open={showNotesDialog}
        notes={notes}
        setNotes={setNotes}
        onClose={() => setShowNotesDialog(false)}
      />

      {/* iter916 — Tailor measurements capture (branch business_type='tailor'). */}
      <TailorMeasurementsDialog
        open={showMeasurements}
        onOpenChange={setShowMeasurements}
        value={saleMetadata}
        onSave={setSaleMetadata}
      />

      {/* Discount sheet (iter378) — extracted iter845. */}
      <PosDiscountSheet
        open={showDiscountSheet}
        discount={discount}
        setDiscount={setDiscount}
        discountType={discountType}
        setDiscountType={setDiscountType}
        currencySymbol={currencySymbol}
        onClose={() => setShowDiscountSheet(false)}
      />
    </div>

    <ReceiptPreview orderId={receiptOrderId} open={!!receiptOrderId} onClose={() => setReceiptOrderId(null)} />
    {/* 4-step "First Sale" walkthrough — opens from the header help icon. */}
    <PosFirstSaleTour open={showTour} onOpenChange={setShowTour} />
    {/* iter937 — Live inter-branch stock-request popup on the POS. */}
    <StockRequestPopup branchId={activeBranchId || user?.branch_id || null} />
    </>
  );
}
